import type {Axis} from '@o2d/datasource';
import type {Viewport} from '@o2d/datasource';
import type {QueryParameter} from './query-parameter.interface';
import type {SortConfig} from './sort-config.types';

/**
 * Axis query configuration types for viewport queries.
 * Sent to backend to describe which data to fetch for a viewport.
 */
export type AxisQueryConfig = DateTimeAxisConfig | EntityAxisConfig;

/**
 * Configuration for date-time based axis (e.g., timeline).
 */
export interface DateTimeAxisConfig {
    type: 'DateTime';
    min: string;  // ISO 8601 format
    max: string;  // ISO 8601 format
}

/**
 * Configuration for entity-based axis (e.g., employees, assignees).
 */
export interface EntityAxisConfig {
    type: 'Entity';
    entityType: string;       // PascalCase: 'Employee' | 'Project' | 'Keyword'
    referenceUid: string;     // ShortId of the reference entity (axis origin)
    minIndex: number;         // Relative to origin
    maxIndex: number;         // Relative to origin
    sort: SortConfig;         // Sort strategy and parameters
    queryParams?: QueryParameter<unknown>[];  // Optional filters for entity axis
}

/**
 * Options object for the entityAxisBuilder factory function.
 */
export interface EntityAxisBuilderOptions {
    /** PascalCase entity type name ('Employee', 'Project', 'Keyword') */
    entityType: string;
    /** Sort configuration (strategy, path, direction) */
    sort: SortConfig;
    /** Optional query parameters for filtering entities */
    queryParams?: QueryParameter<unknown>[];
}

/**
 * Request body for viewport query endpoint.
 */
export interface ViewportQueryRequest {
    xAxis: AxisQueryConfig;
    yAxis: AxisQueryConfig;
    includes?: string[];  // Related entities to include (e.g., 'employees', 'project')
}

/**
 * Response from viewport query endpoint.
 */
export interface ViewportQueryResponse<TItemDTO = unknown> {
    items: TItemDTO[];
    appliedSort?: {
        xAxis?: AppliedSort;
        yAxis?: AppliedSort;
    };
}

/**
 * Information about the sort applied by backend.
 * Used to detect race conditions when axis sort changed during request.
 * Type alias for SortConfig — backend echoes the same sort structure.
 */
export type AppliedSort = SortConfig;

/**
 * Configuration for RestApiCrosspointDataService.
 */
export interface RestApiCrosspointDataServiceConfig {
    xAxisConfig: AxisConfigBuilder;
    yAxisConfig: AxisConfigBuilder;
    includes: string[];
    queryParams?: QueryParameter<unknown>[];
}

/**
 * Builder function for axis configurations.
 * Converts viewport indices to AxisQueryConfig for backend.
 */
export type AxisConfigBuilder = (
    axis: Axis<any, any>,
    viewport: Viewport,
    range: 'x' | 'y'
) => AxisQueryConfig;

/**
 * Pre-built axis config builder for DateTime axes.
 * Extracts min/max values from axis at viewport indices.
 */
export const dateTimeAxisBuilder: AxisConfigBuilder = (axis, viewport, range) => {
    const [minIdx, maxIdx] = range === 'x'
        ? viewport.indexRangeX
        : viewport.indexRangeY;

    const minValue = axis.getValue(minIdx);
    const maxValue = axis.getValue(maxIdx);

    // Explicit null check instead of non-null assertion
    if (!minValue || !maxValue) {
        throw new Error(`Axis values not loaded for indices ${minIdx}-${maxIdx}`);
    }

    return {
        type: 'DateTime',
        min: minValue,
        max: maxValue
    };
};

/**
 * Factory for entity-based axis config builders.
 * Creates a builder that references an entity collection with sorting.
 *
 * @param options - Configuration options for the entity axis builder
 * @returns AxisConfigBuilder function
 */
export const entityAxisBuilder = (
    options: EntityAxisBuilderOptions
): AxisConfigBuilder => (axis, viewport, range) => {
    const [minIdx, maxIdx] = range === 'x'
        ? viewport.indexRangeX
        : viewport.indexRangeY;

    // Origin of axis as reference (stored at index 0) - explicit null check
    const referenceUid = axis.getValue(0);
    if (!referenceUid) {
        throw new Error('Axis origin has no value - axis not initialized');
    }

    const originIndex = 0;  // Origin is always at index 0

    return {
        type: 'Entity',
        entityType: options.entityType,
        referenceUid,
        minIndex: minIdx - originIndex,  // Relative to origin
        maxIndex: maxIdx - originIndex,
        sort: options.sort,
        ...(options.queryParams?.length ? { queryParams: options.queryParams } : {})
    };
};
